import { fetchXaiKey } from '@/lib/themeReference';
import { findForeignThemeSyntax, findInvalidThemeTags } from '@/lib/themeTagValidation';
import { THEME_TAGS } from '@/lib/themeTags';

// ============================================================
// Réparation par IA des balises d'un thème existant.
// Objectif : reconvertir les pages contenant une syntaxe d'un
// autre moteur (Liquid/Shopify : {{merchant.name}}, {{#products}},
// {% if %}) OU des balises Zifek inconnues, vers les VRAIES balises
// officielles du moteur Zifek — sans toucher au design HTML/CSS.
// ============================================================

export interface RepairPageInput {
  page_key: string;
  title: string;
  content: string;
}

export interface RepairPageResult {
  page_key: string;
  content: string;
}

export interface RepairResult {
  pages: RepairPageResult[];
  /** Clés des pages effectivement modifiées */
  changed: string[];
  usedAi: boolean;
  warnings: string[];
  /** Nombre de pages analysées contenant au moins une balise invalide */
  affectedCount: number;
}

const XAI_ENDPOINT = 'https://api.x.ai/v1/chat/completions';
const XAI_MODEL = 'grok-4.6';

/** Vrai si le contenu d'une page nécessite une réparation de balises. */
export function themeNeedsRepair(content: string): boolean {
  if (!content) return false;
  return findForeignThemeSyntax(content).length > 0 || findInvalidThemeTags(content).length > 0;
}

/** Compte le nombre de balises non-Zifek dans un contenu. */
export function countNonZifekTags(content: string): number {
  const foreign = findForeignThemeSyntax(content).reduce((acc, t) => acc + t.count, 0);
  const invalid = findInvalidThemeTags(content).reduce((acc, t) => acc + t.count, 0);
  return foreign + invalid;
}

const TAG_LIST = THEME_TAGS.map((t) => `{{${t.name}}}`).join(' ');

function buildSystemPrompt(): string {
  return `Tu es un expert du moteur de thèmes Zifek. Ta mission : RÉPARER les balises d'une page de thème HTML.

Le moteur Zifek ne comprend QUE ces balises officielles (entre doubles accolades, sans point) :
${TAG_LIST}

Ces balises seules, isolées, remplacent TOUT un composant dynamique (grille produits, liste services, témoignages, blog, équipe, formulaires, etc.).

RÈGLES ABSOLUES :
1. Ne change QUE les balises. Conserve le HTML, les classes CSS, la structure, les textes statiques et les liens (/produits, /panier, /connexion…) EXACTEMENT tels quels.
2. Remplace toute balise d'un autre moteur ({{merchant.name}}, {{cart.item_count}}, {{product.price}}, {{category.name}}, {{customer.name}}, {{post.title}}, {{routes.*}}, {{homepage.*}}…) :
   - par la balise Zifek équivalente quand elle existe (merchant.name → {{site_name}}, merchant.description → {{site_description}}, merchant.phone → {{site_phone}}, merchant.email → {{site_email}}, merchant.address → {{site_address}}, merchant.logo → {{site_logo}}, merchant.website → {{site_url}}, best-sellers/produits → {{products}}, catégories → {{products}}, témoignages → {{testimonials}}, articles → {{blog_posts}}, équipe → {{team}}, contact → {{contact_form}}, année/copyright → {{year}}) ;
   - sinon SUPPRIME simplement la balise (ne laisse jamais {{…}} d'un autre moteur).
3. Supprime tous les marqueurs de section d'un autre moteur : {{#…}}, {{/…}}, {{^…}}, {{else}}, et les blocs {% … %}. Le moteur Zifek gère les boucles lui-même : une seule balise {{products}} remplace l'ensemble {{#products}}…{{/products}}.
4. Ne crée JAMAIS de nouvelle balise. N'invente pas de balise pointée. N'utilise aucune balise absente de la liste ci-dessus.
5. Ne modifie jamais les styles, les images statiques, ni la mise en page.

FORMAT DE RÉPONSE : JSON strict, sans markdown :
{ "pages": { "clé_de_page": "contenu HTML réparé", ... } }
Renvoie UNIQUEMENT les pages fournies, avec la même clé.`;
}

function buildUserContent(targets: RepairPageInput[]): string {
  return targets
    .map((p) => `### ${p.page_key} (${p.title})\n${p.content}`)
    .join('\n\n');
}

/**
 * Répare les balises des pages fournies via l'IA (xAI Grok).
 * Seules les pages contenant au moins une balise non-Zifek sont envoyées.
 */
export async function repairThemeTagsWithAi(pages: RepairPageInput[]): Promise<RepairResult> {
  const targets = pages.filter((p) => themeNeedsRepair(p.content || ''));
  const warnings: string[] = [];

  if (targets.length === 0) {
    return {
      pages: [],
      changed: [],
      usedAi: false,
      warnings: ['Aucune balise non-Zifek détectée — ce thème est déjà propre.'],
      affectedCount: 0,
    };
  }

  const apiKey = await fetchXaiKey();
  if (!apiKey) {
    throw new Error('Aucune clé IA (xAI Grok) configurée. Ajoute-la dans les paramètres de la plateforme.');
  }

  const response = await fetch(XAI_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: XAI_MODEL,
      messages: [
        { role: 'system', content: buildSystemPrompt() },
        { role: 'user', content: buildUserContent(targets) },
      ],
      temperature: 0.2,
      max_tokens: 32000,
      response_format: { type: 'json_object' },
    }),
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Erreur IA ${response.status}: ${errText.slice(0, 180)}`);
  }

  const result = await response.json();
  const content: string = result?.choices?.[0]?.message?.content || '';
  if (!content) throw new Error('Réponse vide de l\'IA');

  let parsed: { pages?: Record<string, string> };
  try {
    parsed = JSON.parse(content);
  } catch {
    const match = content.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (!match) throw new Error('Impossible de lire la réponse JSON de l\'IA');
    parsed = JSON.parse(match[1].trim());
  }

  const aiPages = parsed?.pages || {};
  const results: RepairPageResult[] = [];
  const changed: string[] = [];

  for (const target of targets) {
    const repaired = aiPages[target.page_key];
    if (typeof repaired !== 'string' || repaired.trim().length === 0) {
      warnings.push(`${target.page_key} : pas de correction renvoyée par l'IA.`);
      continue;
    }
    // Sécurité : on n'applique que si la version réparée ne contient plus (ou moins) de balises non-Zifek.
    const before = countNonZifekTags(target.content);
    const after = countNonZifekTags(repaired);
    if (after > 0 && after >= before) {
      warnings.push(`${target.page_key} : correction insuffisante, page conservée à l'identique.`);
      continue;
    }
    if (repaired.trim() !== (target.content || '').trim()) {
      results.push({ page_key: target.page_key, content: repaired.trim() });
      changed.push(target.page_key);
    }
  }

  return {
    pages: results,
    changed,
    usedAi: true,
    warnings,
    affectedCount: targets.length,
  };
}